import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Plus,
  Search,
  UserCheck,
} from 'lucide-react';
import type {
  EmployeeListItem,
  EmployeeStatus,
  EmploymentType,
  PaginatedResponse,
} from '@ip/shared-types';
import { EMPLOYMENT_TYPES, EMPLOYEE_STATUSES } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useEmployeesApi } from '@/services/api';
import { EmployeeStatusBadge } from './EmployeeStatusBadge';
import { EmployeeForm, EmployeeFormValues } from './EmployeeForm';
import { formValuesToEmployeeDto, peselWarningMessage } from './employeeFormUtils';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';

const STORAGE_KEY = 'employee-list-filters';
const CLEAR_VALUE = '__clear__';
const PAGE_SIZES = [25, 50, 100];
const COLUMN_COUNT = 9;

interface Filters {
  search: string;
  type: EmploymentType | null;
  status: EmployeeStatus | 'all' | null;
  includeArchived: boolean;
  page: number;
  pageSize: number;
}

const DEFAULT_FILTERS: Filters = {
  search: '',
  type: null,
  status: null,
  includeArchived: false,
  page: 1,
  pageSize: 50,
};

function loadFilters(companyNip: string): Filters {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}:${companyNip}`);
    return raw ? { ...DEFAULT_FILTERS, ...JSON.parse(raw) } : DEFAULT_FILTERS;
  } catch {
    return DEFAULT_FILTERS;
  }
}

function saveFilters(companyNip: string, f: Filters) {
  try {
    localStorage.setItem(`${STORAGE_KEY}:${companyNip}`, JSON.stringify(f));
  } catch { /* quota issues — ignore */ }
}

export interface EmployeeListProps {
  companyNip: string;
  companyName: string;
}

/** Muted "why is this empty" hint for the payroll columns. */
function PayrollHint({ note }: { note: EmployeeListItem['payrollNote'] }) {
  if (!note) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="text-muted-foreground text-xs">
      {note === 'no_rate' ? t('employees.payroll.noRate') : t('employees.payroll.noType')}
    </span>
  );
}

export function EmployeeList({ companyNip }: EmployeeListProps) {
  const { list, create } = useEmployeesApi();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Filters>(() => loadFilters(companyNip));
  const [data, setData] = useState<PaginatedResponse<EmployeeListItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);

  useEffect(() => { saveFilters(companyNip, filters); }, [companyNip, filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    list(companyNip, {
      search: filters.search || undefined,
      type: filters.type ?? undefined,
      status: filters.status ?? undefined,
      includeArchived: filters.includeArchived || undefined,
      page: filters.page,
      pageSize: filters.pageSize,
    })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((err) => toast.error((err as Error).message))
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [companyNip, filters, list]);

  const handleCreate = async (values: EmployeeFormValues) => {
    setCreateBusy(true);
    try {
      const dto = formValuesToEmployeeDto(values);
      const created = await create(companyNip, dto);
      toast.success(t('common.add') + ' ✓');
      const peselWarning = peselWarningMessage(created);
      if (peselWarning) toast.warning(peselWarning);
      setCreateOpen(false);
      navigate(`/${companyNip}/employees/${created.id}`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setCreateBusy(false);
    }
  };

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / filters.pageSize));
  const from = total === 0 ? 0 : (filters.page - 1) * filters.pageSize + 1;
  const to = Math.min(filters.page * filters.pageSize, total);
  const setPage = (page: number) => setFilters((f) => ({ ...f, page }));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            placeholder={t('employees.filters.search')}
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
            className="w-[280px] pl-9"
          />
        </div>
        <Select
          value={filters.type ?? ''}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              type: v === CLEAR_VALUE ? null : (v as EmploymentType),
              page: 1,
            }))
          }
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t('employees.type.label')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CLEAR_VALUE}>{t('common.all')}</SelectItem>
            {EMPLOYMENT_TYPES.map((tp) => (
              <SelectItem key={tp} value={tp}>
                {t(`employees.type.${tp}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.status ?? ''}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              status: v === CLEAR_VALUE ? null : (v as EmployeeStatus | 'all'),
              page: 1,
            }))
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('employees.status.label')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CLEAR_VALUE}>{t('common.clear')}</SelectItem>
            <SelectItem value="all">{t('employees.status.all')}</SelectItem>
            {EMPLOYEE_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`employees.status.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch
            id="employee-include-archived"
            checked={filters.includeArchived}
            onCheckedChange={(v) => setFilters((f) => ({ ...f, includeArchived: v, page: 1 }))}
          />
          <Label htmlFor="employee-include-archived" className="font-normal">
            {t('employees.filters.includeArchived')}
          </Label>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          {t('employees.addEmployee')}
        </Button>
      </div>

      <div className="bg-card rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-muted-foreground w-[130px] whitespace-nowrap">
                  {t('employees.status.label')}
                </TableHead>
                <TableHead className="text-muted-foreground whitespace-nowrap">
                  {t('employees.identity.firstName') + ' / ' + t('employees.identity.lastName')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[180px] whitespace-nowrap">
                  {t('employees.type.label')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[160px] whitespace-nowrap">
                  {t('employees.rates.amount')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[120px] text-right whitespace-nowrap">
                  {t('employees.payroll.gross')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[120px] text-right whitespace-nowrap">
                  {t('employees.payroll.net')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[140px] text-right whitespace-nowrap">
                  {t('employees.payroll.employerCost')}
                </TableHead>
                <TableHead className="text-muted-foreground w-[150px] whitespace-nowrap">
                  {t('employees.employment.contractExpiryDate')}
                </TableHead>
                <TableHead className="text-muted-foreground whitespace-nowrap">
                  {t('employees.employment.contractor.title')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : (data?.items ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-40 text-center">
                    <p className="font-medium">{t('employees.noEmployees')}</p>
                    <p className="text-muted-foreground text-sm">
                      {t('employees.noEmployeesDescription')}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                (data?.items ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="py-2">
                      <EmployeeStatusBadge status={row.status} />
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/${companyNip}/employees/${row.id}`}
                          className="text-primary font-medium hover:underline"
                        >
                          {row.firstName} {row.lastName}
                        </Link>
                        {row.hasAppAccount && (
                          <UserCheck
                            className="text-muted-foreground size-3.5 shrink-0"
                            aria-label={t('employees.appAccount')}
                          />
                        )}
                      </div>
                      {row.workEmail && (
                        <p className="text-muted-foreground text-xs">{row.workEmail}</p>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      {row.employmentType
                        ? t(`employees.type.${row.employmentType}`)
                        : <span className="text-muted-foreground">{t('employees.type.notSet')}</span>}
                    </TableCell>
                    <TableCell className="py-2 tabular-nums">
                      {row.currentRate
                        ? `${formatPLN(row.currentRate.amount)} ${row.currentRate.rateType === 'hourly' ? '/h' : '/mo'}`
                        : '—'}
                    </TableCell>
                    <TableCell className="py-2 text-right tabular-nums">
                      {row.payroll ? formatPLN(row.payroll.gross) : <PayrollHint note={row.payrollNote} />}
                    </TableCell>
                    <TableCell className="py-2 text-right tabular-nums">
                      {row.payroll ? formatPLN(row.payroll.net) : <PayrollHint note={row.payrollNote} />}
                    </TableCell>
                    <TableCell className="py-2 text-right font-medium tabular-nums">
                      {row.payroll ? formatPLN(row.payroll.employerCost) : <PayrollHint note={row.payrollNote} />}
                    </TableCell>
                    <TableCell className="py-2">{row.contractExpiryDate}</TableCell>
                    <TableCell className="py-2">
                      {row.contractor ? row.contractor.vendorName : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2">
          <p className="text-muted-foreground text-sm">
            {t('pagination.showing', { from, to, total })}
          </p>
          <div className="flex items-center gap-2">
            <Select
              value={String(filters.pageSize)}
              onValueChange={(v) => setFilters((f) => ({ ...f, pageSize: Number(v), page: 1 }))}
            >
              <SelectTrigger size="sm" className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="size-8" disabled={filters.page <= 1} onClick={() => setPage(1)}>
                <ChevronsLeft className="size-4" />
              </Button>
              <Button variant="outline" size="icon" className="size-8" disabled={filters.page <= 1} onClick={() => setPage(filters.page - 1)}>
                <ChevronLeft className="size-4" />
              </Button>
              <span className="px-2 text-sm tabular-nums">
                {filters.page} / {totalPages}
              </span>
              <Button variant="outline" size="icon" className="size-8" disabled={filters.page >= totalPages} onClick={() => setPage(filters.page + 1)}>
                <ChevronRight className="size-4" />
              </Button>
              <Button variant="outline" size="icon" className="size-8" disabled={filters.page >= totalPages} onClick={() => setPage(totalPages)}>
                <ChevronsRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('employees.addEmployee')}</DialogTitle>
          </DialogHeader>
          <EmployeeForm
            formId="employee-create-form"
            companyNip={companyNip}
            initialValues={null}
            onSubmit={handleCreate}
            disabled={createBusy}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="employee-create-form" disabled={createBusy}>
              {t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
